import { Box, Card, Typography } from '@vp/swan';
import { getOrCreateFormattedTimezone, getTimeOfDayIcon } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';

interface DesignLiveSessionConfirmationProps {
    time: string;
    timezone: string;
    designerName?: string;
}

function normalizeAmPm(str: string): string {
    return str.replace('a.m.', 'AM').replace('p.m.', 'PM').replace('am', 'AM').replace('pm', 'PM');
}

export const DesignLiveSessionConfirmation = ({
    time,
    timezone,
    designerName,
}: DesignLiveSessionConfirmationProps) => {
    const { locale } = usePage();
    const date = new Date(time);

    let dateDisplay: string;
    let timeDisplay: string;
    let timezoneDisplay: string;

    try {
        dateDisplay = date.toLocaleDateString(locale, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: timezone,
        });

        timeDisplay = `${normalizeAmPm(
            new Intl.DateTimeFormat(locale, {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
                timeZone: timezone,
            }).format(date)
        )} ${getTimeOfDayIcon(timezone, date)}`;

        timezoneDisplay = getOrCreateFormattedTimezone(timezone, date, locale).displayName;
    } catch {
        // timezone is not a valid IANA string — fall back to a safe UTC display
        dateDisplay = date.toLocaleDateString(locale, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
        });

        timeDisplay = normalizeAmPm(
            new Intl.DateTimeFormat(locale, {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
                timeZone: 'UTC',
            }).format(date)
        );

        timezoneDisplay = 'UTC';
    }

    return (
        <Card backgroundColor="accent">
            <Typography fontSkin="body-standard-bold" mb={4}>
                {__('Your DesignLive session will be scheduled for:', {
                    _context:
                        'Heading on a confirmation card shown after a customer selects a DesignLive appointment date and time',
                })}
            </Typography>
            <Box
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    rowGap: '4px',
                    columnGap: '16px',
                }}
            >
                <Typography fontSkin="body-standard-bold">
                    {__('Date:', {
                        _context:
                            'Label for the date in a DesignLive appointment confirmation card, e.g. "Date: Friday, April 20, 2026"',
                    })}
                </Typography>
                <Typography>{dateDisplay}</Typography>
                <Typography fontSkin="body-standard-bold">
                    {__('Session starts:', {
                        _context:
                            'Label for the start time in a DesignLive appointment confirmation card, e.g. "Session starts: 11:00 pm"',
                    })}
                </Typography>
                <Typography>{timeDisplay}</Typography>
                <Typography fontSkin="body-standard-bold">
                    {__('Timezone:', {
                        _context:
                            'Label for the timezone in a DesignLive appointment confirmation card, e.g. "Timezone: Pacific Time (US & Canada)"',
                    })}
                </Typography>
                <Typography>{timezoneDisplay}</Typography>
                {designerName && (
                    <>
                        <Typography fontSkin="body-standard-bold">
                            {__('Designer:', {
                                _context:
                                    'Label for the designer in a DesignLive appointment confirmation card, e.g. "Designer: Anna Gomez"',
                            })}
                        </Typography>
                        <Typography>{designerName}</Typography>
                    </>
                )}
            </Box>
        </Card>
    );
};
